import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import {
  ActionIcon,
  Badge,
  Button,
  Collapse,
  Group,
  NumberInput,
  Paper,
  Stack,
  Switch,
  Text,
  Textarea,
} from "@mantine/core"
import { IconChevronDown } from "@tabler/icons-react"
import { useState } from "react"
import type { IReportTypeRow, IViewProps } from "../interfaces"
import classes from "./styles.module.css"

interface ICardProps {
  row: IReportTypeRow
  expanded: boolean
  onToggleExpand: () => void
  onChange: (change: Partial<IReportTypeRow>) => void
}

function ReportTypeCard({ row, expanded, onToggleExpand, onChange }: ICardProps): React.JSX.Element {
  const { i18n } = useLingui()

  return (
    <Paper withBorder p="sm" className={classes.card} data-disabled={!row.enabled}>
      <Group gap="sm" wrap="nowrap" align="flex-start">
        <Switch
          checked={row.enabled}
          onChange={(e) => onChange({ enabled: e.currentTarget.checked })}
          aria-label={i18n._(msg`Offer this report type`)}
          mt={4}
        />
        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs" wrap="wrap">
            <Text size="sm" fw={600} c={row.enabled ? undefined : "dimmed"}>
              {row.label}
            </Text>
            <Badge variant="light" color={row.isBuiltin ? "gray" : "brand"} size="sm">
              {row.isBuiltin ? <Trans>Built-in</Trans> : <Trans>Custom</Trans>}
            </Badge>
          </Group>
          <Text size="xs" c="dimmed">
            {row.audience}
          </Text>
        </Stack>
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={onToggleExpand}
          aria-label={i18n._(msg`Edit prompts and threshold`)}
          aria-expanded={expanded}
          className={classes.chevron}
          data-expanded={expanded}
        >
          <IconChevronDown size={18} />
        </ActionIcon>
      </Group>

      <Collapse in={expanded}>
        <Stack gap="md" mt="md">
          <NumberInput
            label={i18n._(msg`Suggestion threshold`)}
            description={i18n._(msg`Only suggest this report when detection confidence is at least this percent.`)}
            value={row.threshold}
            onChange={(value) => onChange({ threshold: typeof value === "number" ? value : Number(value) || 0 })}
            min={0}
            max={100}
            clampBehavior="strict"
            suffix="%"
            w={160}
          />
          <Textarea
            label={i18n._(msg`Fit prompt`)}
            description={i18n._(msg`Describes when a meeting suits this type. Leave blank to inherit the built-in.`)}
            value={row.fitPrompt}
            onChange={(e) => onChange({ fitPrompt: e.currentTarget.value })}
            autosize
            minRows={2}
            maxRows={8}
          />
          <Textarea
            label={i18n._(msg`Generation prompt`)}
            description={i18n._(
              msg`Instructs the generator how to render this report. Leave blank to inherit the built-in.`,
            )}
            value={row.generationPrompt}
            onChange={(e) => onChange({ generationPrompt: e.currentTarget.value })}
            autosize
            minRows={3}
            maxRows={12}
          />
        </Stack>
      </Collapse>
    </Paper>
  )
}

/**
 * Dumb admin view for the report-type registry: one card per resolved type with
 * an enable/disable toggle, a builtin/custom badge, and collapsible editors for
 * the suggestion threshold and the two prompts (fit + generation). The smart
 * wrapper owns the GET/PUT and the override diff; this view is controlled (rows
 * in, events out).
 */
export function ReportTypesPanelView({
  rows,
  saving,
  dirty,
  bestModelEnabled,
  onRowsChange,
  onBestModelChange,
  onSave,
  onReset,
}: IViewProps): React.JSX.Element {
  const { i18n } = useLingui()
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const toggleExpand = (id: string): void =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const patch = (id: string, change: Partial<IReportTypeRow>): void =>
    onRowsChange(rows.map((row) => (row.id === id ? { ...row, ...change } : row)))

  return (
    <Stack gap="md">
      <div>
        <Text fw={600}>
          <Trans>Report types</Trans>
        </Text>
        <Text size="sm" c="dimmed">
          <Trans>
            Choose which visual report types this workspace offers, tune how confident detection must be before each is
            suggested, and edit the prompts that detect and generate them. This applies to everyone in your workspace.
          </Trans>
        </Text>
      </div>

      <Paper withBorder p="sm">
        <Group justify="space-between" wrap="nowrap" align="flex-start" gap="sm">
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" fw={600}>
              <Trans>Allow the Heavy model</Trans>
            </Text>
            <Text size="xs" c="dimmed">
              <Trans>
                Let people spend about twice the credits to generate a single report on the Heavy model — sharper
                synthesis and richer diagrams. Reports always default to the standard model; this only offers the
                upgrade as a choice.
              </Trans>
            </Text>
          </div>
          <Switch
            checked={bestModelEnabled}
            onChange={(e) => onBestModelChange(e.currentTarget.checked)}
            aria-label={i18n._(msg`Allow the Heavy model for reports`)}
            mt={4}
          />
        </Group>
        {bestModelEnabled && (
          <Text size="xs" c="dimmed" mt="sm">
            <Trans>Uses your Heavy model — choose it under Settings → Model selection.</Trans>
          </Text>
        )}
      </Paper>

      <Stack gap="xs">
        {rows.map((row) => (
          <ReportTypeCard
            key={row.id}
            row={row}
            expanded={expanded.has(row.id)}
            onToggleExpand={() => toggleExpand(row.id)}
            onChange={(change) => patch(row.id, change)}
          />
        ))}
      </Stack>

      <Group>
        <Button onClick={onSave} loading={saving} disabled={!dirty}>
          <Trans>Save report types</Trans>
        </Button>
        <Button variant="default" onClick={onReset} disabled={!dirty || saving}>
          <Trans>Reset</Trans>
        </Button>
      </Group>
    </Stack>
  )
}
